import React from 'react';
import { RefreshCw, Plus, Crown } from 'lucide-react';

interface ActionButton {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'accent' | 'secondary';
  disabled?: boolean;
  loading?: boolean;
  isPremium?: boolean;
  requiresProfessional?: boolean;
}

interface SectionHeaderProps {
  title: string;
  description: string;
  language: 'ar' | 'en';
  isRTL: boolean;
  actionButtons?: ActionButton[];
  refreshButton?: {
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
  };
  addButton?: {
    label: string;
    onClick: () => void;
  };
  children?: React.ReactNode; // For filters, search bars, etc.
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  description,
  language,
  isRTL,
  actionButtons = [],
  refreshButton,
  addButton,
  children
}) => {
  const getButtonStyles = (variant: 'primary' | 'accent' | 'secondary' = 'secondary') => {
    switch (variant) {
      case 'primary':
        return 'bg-primary text-white hover:bg-primary-dark transition-colors';
      case 'accent':
        return 'bg-accent text-primary hover:bg-accent/80 transition-colors';
      case 'secondary':
      default:
        return 'bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-600';
    }
  };

  return (
    <div className="mb-8">
      {/* Title, Description and Action Buttons */}
      <div className={`flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
            {title}
          </h1>
          <p className="text-slate-600 dark:text-slate-300">
            {description}
          </p>
        </div>
        
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Custom Action Buttons */}
          {actionButtons.map((button, index) => (
            <button
              key={index}
              onClick={button.onClick}
              disabled={button.disabled || button.requiresProfessional}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed relative ${getButtonStyles(button.variant)}`}
              title={button.requiresProfessional ? (language === 'ar' ? 'يتطلب الخطة المهنية' : 'Requires Professional Plan') : undefined}
            >
              {button.loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  {button.icon}
                  {(button.isPremium || button.requiresProfessional) && (
                    <Crown size={14} className="text-accent" />
                  )}
                </span>
              )}
              {button.label}
            </button>
          ))}

          {/* Refresh Button */}
          {refreshButton && (
            <button
              onClick={refreshButton.onClick}
              disabled={refreshButton.disabled}
              className="flex items-center gap-2 px-4 py-2 bg-accent text-primary rounded-lg hover:bg-accent/80 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              title={language === 'ar' ? 'تحديث' : 'Refresh'}
            >
              <RefreshCw className={`w-4 h-4 ${refreshButton.loading ? 'animate-spin' : ''}`} />
              {language === 'ar' ? 'تحديث' : 'Refresh'}
            </button>
          )}

          {/* Add Button */}
          {addButton && (
            <button
              onClick={addButton.onClick}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              <Plus className="w-4 h-4" />
              {addButton.label}
            </button>
          )}
        </div>
      </div>
      
      {/* Filters, Search Bars, etc. */}
      {children && (
        <div className="mb-6">
          {children}
        </div>
      )}
    </div>
  );
};

export default SectionHeader;